import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { geoMercator, GeoPath, geoPath } from 'd3-geo'

import { PlaceLabel } from 'components/PlaceLabel'
import { RegionLabel } from 'components/RegionLabel'
import { useElementSize } from 'usehooks-ts'
import * as d3 from 'd3'
import { EnergyAttacks } from 'Types/EnergyAttacks'

const BaseMap = require('../GeoJSON/country.json')
const RegionsMap = require('../GeoJSON/regions.json')
const AreaOfInterest = BaseMap //require('../GeoJSON/AreaOfInterest.json')

const UkraineMap = ({
  data,
  powerPlants,
  height,
  russianControl,
  russianAdvance,
  regionLabels,
  mapLabels,
  colorMode,
  index,
  zoomToRegionLabels,
  colorPalette,
  sizeFactor = 0.006,
  startTimestamp,
  pointOfInterestJson,
}: {
  data?: EnergyAttacks
  powerPlants?: any
  height: number
  russianControl?: any
  russianAdvance?: any
  regionLabels?: RegionLabelType[]
  mapLabels?: MapLabelType[]
  colorMode: 'light' | 'dark'
  index: number
  zoomToRegionLabels?: boolean
  colorPalette?: { [key: string]: { dark: string; light: string } }
  sizeFactor?: number
  startTimestamp?: number
  pointOfInterestJson?: string
}) => {
  const [container, size] = useElementSize()
  const [isMobile, setMobile] = useState(false)
  const [{ width }, setSize] = useState({
    width: 100,
  })
  const bombsRef = React.useRef<SVGGElement | null>(null)
  const svgRef = React.useRef<SVGSVGElement | null>(null)
  const regionLabelsRef = React.useRef<SVGGElement | null>(null)
  const pointOfInterestRef = React.useRef<SVGGElement | null>(null)
  let projection = geoMercator().fitSize([width, height], AreaOfInterest as any)
  useEffect(() => {
    if (pointOfInterestJson) {
      const distanceToObject = isMobile ? 10 : 0

      const pointOfInterestSelection = d3
        .select(pointOfInterestRef.current)
        .node() as SVGGraphicsElement
      const bboxEast = pointOfInterestSelection.getBBox()
      d3.select(svgRef.current)
        .transition()
        .ease(d3.easeCubicOut)
        .duration(1000)
        .attr(
          'viewBox',
          bboxEast.x -
            distanceToObject +
            ' ' +
            (bboxEast.y - distanceToObject) +
            ' ' +
            (bboxEast.width + distanceToObject * 2) +
            ' ' +
            (bboxEast.height + distanceToObject * 2)
        )
    }
  }, [pointOfInterestJson])
  useEffect(() => {
    const distanceToObject = isMobile ? 50 : 250

    if (isMobile) {
      const regionLabelsSelection = d3
        .select(regionLabelsRef.current)
        .node() as SVGGraphicsElement

      const bbox = regionLabelsSelection.getBBox()
      if (
        svgRef?.current &&
        regionLabelsRef?.current &&
        regionLabels &&
        zoomToRegionLabels &&
        !pointOfInterestJson
      ) {
        d3.select(svgRef.current)
          .transition()
          .ease(d3.easeCubicOut)
          .duration(1000)
          .attr(
            'viewBox',
            bbox.x -
              distanceToObject +
              ' ' +
              (bbox.y - distanceToObject) +
              ' ' +
              (bbox.width + distanceToObject * 2) +
              ' ' +
              (bbox.height + distanceToObject * 2)
          )
      } else {
        d3.select(svgRef.current)
          .transition()
          .ease(d3.easeCubicOut)
          .duration(1000)
          .attr('viewBox', '0 0 ' + width + ' ' + height)
      }
    }
  }, [regionLabels])
  useEffect(() => {
    if (size.width === width) return

    setSize({ width: size.width })
    setMobile(size.width < 712)
    projection = geoMercator().fitSize(
      [size.width, size.height],
      AreaOfInterest as any
    )
  }, [size])
  const geoPathGenerator: GeoPath<any, any> = geoPath().projection(projection)
  useEffect(() => {
    if (height <= 0) return
    let projection = geoMercator().fitSize(
      [width, height],
      AreaOfInterest as any
    )

    // staggered size in animation of bombs
    if (bombsRef?.current && data && startTimestamp) {
      d3.select(bombsRef.current)
        .selectAll('circle')
        .data(data.features)
        .attr('r', (d: any) => {
          return d.properties.timestamp > startTimestamp ? 0 : 3
        })
        .transition()
        .duration(1000)
        .ease(d3.easeBounceOut)
        .delay(
          (d, i) =>
            ((d?.properties?.timestamp ?? startTimestamp) - startTimestamp) /
            5000
        )
        .attr('r', (d: any) => {
          return isMobile ? 3 : 4
        })
        .attr('opacity', (d: any) => {
          return d.properties.timestamp > startTimestamp ? 1 : 0.3
        })
    }
  }, [height, data?.features?.length])

  return (
    <MapContainer ref={container} className='MapContainer'>
      <StyledSVG
        ref={svgRef}
        width={width}
        height={height}
        viewBox={'0 0 ' + width + ' ' + height}
      >
        <defs>
          <pattern
            id='diagonal-stripe-1'
            patternUnits='userSpaceOnUse'
            width='4'
            height='4'
          >
            <path
              d='M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2'
              style={{
                stroke: colorMode === 'light' ? '#6f7187' : '#EFECE0',
                strokeWidth: 1.5,
              }}
            />
          </pattern>
        </defs>
        <g>
          {pointOfInterestJson && (
            <g ref={pointOfInterestRef} className='point-of-interest'>
              <Regions
                d={geoPathGenerator(pointOfInterestJson) ?? undefined}
                colorMode={colorMode}
              />
            </g>
          )}
          <g className='regions'>
            <Regions
              d={geoPathGenerator(RegionsMap) ?? undefined}
              colorMode={colorMode}
            />
          </g>
          <g className='occupied'>
            {russianControl && (
              <Occupied
                d={geoPathGenerator(russianControl) ?? undefined}
                colorMode={colorMode}
              />
            )}
          </g>
          <g className='advance'>
            {russianAdvance && (
              <Advances
                d={geoPathGenerator(russianAdvance) ?? undefined}
                colorMode={colorMode}
              />
            )}
          </g>
          <g className='country'>
            <CountrySecondOutline d={geoPathGenerator(BaseMap) ?? undefined} />
            <CountryOutline d={geoPathGenerator(BaseMap) ?? undefined} />
          </g>

          {powerPlants && (
            <g className='powerplants'>
              {powerPlants.features.map(
                ({ geometry, properties }: any, i: number) => {
                  let pos = projection(geometry.coordinates)

                  if (
                    !pos ||
                    pos?.some(
                      (coord) =>
                        isNaN(coord) ||
                        isNaN(+properties['Total Capacity (MW)'])
                    )
                  ) {
                    return null
                  }
                  const color =
                    colorPalette?.[properties['Fuel Type']][colorMode]
                  const radius = +properties['Total Capacity (MW)'] * sizeFactor

                  return (
                    <Marker
                      key={'PowerPlant_' + i}
                      cx={pos[0]}
                      cy={pos[1]}
                      r={radius}
                      opacity={0.8}
                      color={color ?? undefined}
                    />
                  )
                }
              )}
            </g>
          )}
          {data?.features && (
            <g className='bombs' ref={bombsRef}>
              {data.features.map(({ geometry, properties }, i: number) => {
                let pos = projection(geometry.coordinates)
                if (pos?.some((coord) => isNaN(coord))) {
                  return null
                }
                const type = properties['gas|pipeline']
                  ? '_gas'
                  : properties['water']
                  ? '_water'
                  : '_electricity'
                const color = colorPalette?.['bomb' + type]?.[colorMode]
                const radius = isMobile ? 3 : 4
                return (
                  <React.Fragment key={'Bomb_' + i}>
                    {index !== 16 && pos?.some((coord) => coord !== NaN) && (
                      <Bomb pos={pos} r={radius} color={color ?? undefined} />
                    )}
                    {index === 16 && pos?.some((coord) => coord !== NaN) && (
                      <Building colorMode={colorMode} pos={pos} />
                    )}
                  </React.Fragment>
                )
              })}
            </g>
          )}
          <g ref={regionLabelsRef} className='region_labels'>
            {regionLabels &&
              regionLabels.map((m, i) => {
                if (!m.pos) return null
                let pos = projection(m.pos)
                return (
                  <React.Fragment key={'label_' + i + m.pos}>
                    {m.r && (
                      <RegionLabel
                        x={pos?.[0] ?? 0}
                        y={pos?.[1] ?? 0}
                        data={{
                          pos: m.pos,
                          r: [
                            (width / 100) * (m?.r?.[0] ?? 1),
                            (width / 100) * (m?.r?.[1] ?? 1),
                          ],
                        }}
                      />
                    )}
                    {m.label && (
                      <PlaceLabel
                        x={pos?.[0] ?? 0}
                        y={pos?.[1] ?? 0}
                        hideLine={m.hideLine}
                        offset={m.offset}
                        label={m.label}
                      />
                    )}
                  </React.Fragment>
                )
              })}
          </g>
        </g>
      </StyledSVG>
    </MapContainer>
  )
}
export default React.memo(UkraineMap)
const Building = ({
  colorMode,
  pos,
}: {
  colorMode: 'light' | 'dark'
  pos: [number, number]
}) => {
  return (
    <path
      transform={`translate(${pos[0]},${pos[1]})`}
      d={
        'M4.9137,3.4993l1.793-1.793c.3901-.3896,.3901-1.0244,0-1.4141-.3896-.3896-1.0244-.3896-1.4141,0l-1.793,1.793L1.7067,.2922C1.317-.0974,.6823-.0974,.2926,.2922-.0975,.6819-.0975,1.3167,.2926,1.7063l1.793,1.793L.2926,5.2922c-.3901,.3896-.3901,1.0244,0,1.4141,.3779,.3779,1.0361,.3779,1.4141,0l1.793-1.793,1.793,1.793c.189,.189,.4399,.293,.707,.293s.5181-.104,.707-.293c.3901-.3896,.3901-1.0244,0-1.4141l-1.793-1.793Z'
      }
      opacity={0.9}
      fill={colorMode === 'light' ? '#D3343F' : '#D3343F'}
    />
  )
}
const Bomb = ({
  pos,
  r,
  color,
}: {
  pos: [number, number]
  r: number
  color?: string
}) => {
  return <Marker cx={pos[0]} cy={pos[1]} r={r} opacity={0.9} color={color} />
}
const MapContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 90%;
  max-height: 100vh;
  display: inline-block;
  flex: 0 0 53%;
  margin-bottom: 30px;
  /* overflow: hidden; */
  @media only screen and (max-width: 711px) {
    flex: 0 0 45%;
  }
`
const StyledSVG = styled.svg`
  overflow: visible;
  /* mask-image: linear-gradient(to left, black 92%, transparent 100%); */
  /* border: 1px solid #e5e5e5; */
`

const CountryOutline = styled.path`
  fill: none;
  stroke-linejoin: round;
  stroke: #7d7d7d;
  stroke: var(--text-color);
  opacity: 0.7;
`
const CountrySecondOutline = styled.path`
  fill: none;
  stroke-linejoin: round;
  opacity: 0.7;
  stroke: var(--site-background);
  stroke-width: 5;
  opacity: 0.7;
`
const Regions = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: var(--site-background);
  opacity: 1;
  stroke: ${({ colorMode }) => (colorMode === 'light' ? '#d4d4d4' : '#3e3e3e')};
  stroke-width: 1;
`

const Occupied = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) => (colorMode === 'light' ? '#6f7187' : '#EFECE0')};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.3 : 0.15)};
  stroke: var(--site-background);
  stroke-width: 2;
`
const Advances = styled.path<{ colorMode: 'light' | 'dark' }>`
  fill: ${({ colorMode }) => (colorMode === 'light' ? '#6f7187' : '#EFECE0')};
  opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.4 : 0.2)};
  fill: url(#diagonal-stripe-1);
  stroke: none;
  stroke-width: 1;
  animation: fade-in-low 0.5s cubic-bezier(0.39, 0.575, 0.565, 1) both;
  animation-delay: 0.1s;
  @keyframes fade-in-low {
    0% {
      opacity: 0;
    }
    100% {
      opacity: ${({ colorMode }) => (colorMode === 'light' ? 0.4 : 0.2)};
    }
  }
`
const Marker = styled.circle<{
  color?: string
}>`
  /* @keyframes scale-in-center {
    0% {
      r: 0;
    }
    100% {
      r: 3;
    }
  }

  animation: scale-in-center 2s cubic-bezier(0.130, 1.650, 0.755, 0.905) both; */
  fill: ${({ color }) => color ?? '#E78D38'};
`
